/**
 * Regras do módulo Vendas (propostas de plano de saúde), em funções puras.
 *
 * Vieram do artefato "Minhas Vendas" do claude.ai e valem igual na API e, a partir
 * da V1, na tela (o `apps/web` importa este arquivo). Por isso: nada de Nest, TypeORM
 * ou Node aqui — só TypeScript.
 *
 * Datas de vigência são `AAAA-MM-DD` (sem fuso). "Hoje" é sempre o de Fortaleza,
 * para o "vigência em N dias" não virar um dia perto da meia-noite.
 */

export const SALES_TIME_ZONE = 'America/Fortaleza';

/** Colunas do quadro, nesta ordem. */
export const PROPOSAL_STATUSES = [
  'PRE',
  'ATRIBUIDO',
  'PENDENTE',
  'ASSINAR',
  'BOLETO',
  'ENCERRADO',
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  PRE: 'Pré',
  ATRIBUIDO: 'Atribuído',
  PENDENTE: 'Pendente',
  ASSINAR: 'Assinar contrato',
  BOLETO: 'Boleto',
  ENCERRADO: 'Encerrado',
};

/** MPE = empresarial (PME/CNPJ); PF = individual/familiar; ADESAO = coletivo por adesão. */
export const PROPOSAL_TYPES = ['MPE', 'PF', 'ADESAO'] as const;
export type ProposalType = (typeof PROPOSAL_TYPES)[number];

/** L = limpa (comissionável); R = repique; IN = inadimplente. */
export const BENEFICIARY_SITUATIONS = ['L', 'R', 'IN'] as const;
export type BeneficiarySituation = (typeof BENEFICIARY_SITUATIONS)[number];

/** Atalhos da janela de pendência (somam ao texto com " · "). */
export const PENDING_SHORTCUTS = [
  'Documento',
  'Assinatura',
  'Declaração de saúde',
  'Comprovante de residência',
  'Contrato social / CNPJ',
  'Dados divergentes',
];

/** Dias de vigência da Unimed. */
export const EFFECTIVE_DAYS = ['05', '10', '15', '20', '25', '30'];

/** Nºs genéricos que não contam como duplicidade. */
export const GENERIC_REQUEST_NUMBERS = ['10', '1000'];

/** Faixa de comissão: até `upTo` vidas limpas no mês (null = sem teto), cada vida vale `perLifeCents`. */
export interface CommissionTier {
  upTo: number | null;
  perLifeCents: number;
}

/** Dias no mesmo status a partir dos quais a proposta está parada (Encerrado não para). */
export type StaleDays = Partial<Record<Exclude<ProposalStatus, 'ENCERRADO'>, number>>;

export interface SalesSettingsValues {
  commissionTiers: CommissionTier[];
  staleDays: StaleDays;
  operators: string[];
}

export const DEFAULT_SALES_SETTINGS: SalesSettingsValues = {
  commissionTiers: [
    { upTo: 10, perLifeCents: 10_000 },
    { upTo: 20, perLifeCents: 15_000 },
    { upTo: null, perLifeCents: 20_000 },
  ],
  staleDays: { PRE: 3, ATRIBUIDO: 5, PENDENTE: 2, ASSINAR: 2, BOLETO: 2 },
  operators: ['Unimed Fortaleza', 'Unimed Ceará', 'Hapvida', 'Amil', 'Bradesco', 'SulAmérica'],
};

/** O mínimo de uma proposta que as regras leem — serve para a entidade e para a tela. */
export interface ProposalLike {
  status: ProposalStatus;
  effectiveDate: string | null;
  statusSince?: string | Date | null;
  lives: number;
  beneficiaries: Array<{ situation: BeneficiarySituation | null; monthlyCents: number }>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// ------------------------------------------------------------------ datas

/** "Hoje" em Fortaleza, `AAAA-MM-DD`. */
export function todayIn(now: Date = new Date(), timeZone = SALES_TIME_ZONE): string {
  // en-CA formata como AAAA-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

const dateToMs = (iso: string | null | undefined): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

/** `AAAA-MM-DD` que existe de verdade no calendário. */
export function isValidDate(iso: string | null | undefined): boolean {
  const ms = dateToMs(iso);
  return ms !== null && new Date(ms).toISOString().slice(0, 10) === iso;
}

/** Dias até a vigência (negativo = já passou), contados a partir de `today`. */
export function daysUntil(effectiveDate: string | null, today: string): number | null {
  const target = dateToMs(effectiveDate);
  const base = dateToMs(today);
  return target === null || base === null ? null : Math.round((target - base) / DAY_MS);
}

/** Dias completos no status atual (null quando não se sabe desde quando). */
export function daysInStatus(
  statusSince: string | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!statusSince) return null;
  const since = statusSince instanceof Date ? statusSince.getTime() : Date.parse(statusSince);
  return Number.isNaN(since) ? null : Math.floor((now.getTime() - since) / DAY_MS);
}

/** `AAAA-MM` do mês de uma data `AAAA-MM-DD`. */
export const monthOf = (date: string | null | undefined): string | null =>
  date && /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : null;

/** O mês seguinte a `AAAA-MM`. */
export function nextMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  return mon === 12 ? `${year + 1}-01` : `${year}-${String(mon + 1).padStart(2, '0')}`;
}

/** Sugestões de vigência: todas as do mês atual e as duas primeiras do seguinte. */
export function effectiveDateSuggestions(today: string): string[] {
  const month = today.slice(0, 7);
  const following = nextMonth(month);
  return [
    ...EFFECTIVE_DAYS.map((day) => `${month}-${day}`),
    ...EFFECTIVE_DAYS.slice(0, 2).map((day) => `${following}-${day}`),
  ];
}

// ------------------------------------------------------------ comissão

const cleanLivesOf = (proposal: ProposalLike): number =>
  proposal.beneficiaries.filter((beneficiary) => beneficiary.situation === 'L').length;

/** Valor por vida da faixa atingida: todas as vidas do mês valem a mesma faixa. */
export function perLifeFor(cleanLives: number, tiers: CommissionTier[]): number {
  const tier = tiers.find((item) => item.upTo === null || cleanLives <= item.upTo);
  return (tier ?? tiers[tiers.length - 1]).perLifeCents;
}

export interface CommissionResult {
  month: string;
  /** Vidas limpas de propostas Encerradas com vigência no mês. */
  cleanLives: number;
  perLifeCents: number;
  totalCents: number;
  /** "Faltam X vidas para todas valerem R$ Y"; null na faixa máxima. */
  next: { missing: number; perLifeCents: number } | null;
  /** Vidas limpas de propostas do mês ainda não encerradas. */
  openCleanLives: number;
  /** Se as em andamento fecharem. */
  projectedCents: number;
}

/**
 * Comissão de UM vendedor no mês (a Univendas paga pelos números de cada um — Q5).
 * Quem chama passa só as propostas daquele vendedor.
 */
export function commissionFor(
  proposals: ProposalLike[],
  month: string,
  tiers: CommissionTier[] = DEFAULT_SALES_SETTINGS.commissionTiers,
): CommissionResult {
  const ofMonth = proposals.filter((proposal) => monthOf(proposal.effectiveDate) === month);
  const cleanLives = ofMonth
    .filter((proposal) => proposal.status === 'ENCERRADO')
    .reduce((sum, proposal) => sum + cleanLivesOf(proposal), 0);
  const openCleanLives = ofMonth
    .filter((proposal) => proposal.status !== 'ENCERRADO')
    .reduce((sum, proposal) => sum + cleanLivesOf(proposal), 0);

  const perLifeCents = perLifeFor(cleanLives, tiers);
  const nextTierIndex = tiers.findIndex(
    (item) => item.upTo !== null && cleanLives <= item.upTo,
  );
  const nextTier = nextTierIndex >= 0 ? tiers[nextTierIndex + 1] : undefined;
  const limit = nextTierIndex >= 0 ? tiers[nextTierIndex].upTo : null;
  const projected = cleanLives + openCleanLives;

  return {
    month,
    cleanLives,
    perLifeCents,
    totalCents: cleanLives * perLifeCents,
    next:
      nextTier && limit !== null
        ? { missing: limit + 1 - cleanLives, perLifeCents: nextTier.perLifeCents }
        : null,
    openCleanLives,
    projectedCents: projected * perLifeFor(projected, tiers),
  };
}

// --------------------------------------------------------- resumos e KPIs

export interface LivesSummary {
  proposals: number;
  lives: number;
  clean: number;
  rebound: number;
  defaulting: number;
  /** Beneficiários sem situação marcada (vieram da planilha). */
  unmarked: number;
  totalCents: number;
  cleanCents: number;
  /** Repique + inadimplente: somam no total, mas não no limpo. */
  otherCents: number;
}

/** "X propostas · Y vidas (A limpas, B repique e C inadimplentes)" e os valores. */
export function summarizeLives(proposals: ProposalLike[]): LivesSummary {
  const summary: LivesSummary = {
    proposals: proposals.length,
    lives: 0,
    clean: 0,
    rebound: 0,
    defaulting: 0,
    unmarked: 0,
    totalCents: 0,
    cleanCents: 0,
    otherCents: 0,
  };
  for (const proposal of proposals) {
    summary.lives += proposal.lives || proposal.beneficiaries.length;
    for (const beneficiary of proposal.beneficiaries) {
      const cents = Math.abs(beneficiary.monthlyCents || 0);
      summary.totalCents += cents;
      if (beneficiary.situation === 'L') {
        summary.clean += 1;
        summary.cleanCents += cents;
      } else {
        summary.otherCents += cents;
        if (beneficiary.situation === 'R') summary.rebound += 1;
        else if (beneficiary.situation === 'IN') summary.defaulting += 1;
        else summary.unmarked += 1;
      }
    }
  }
  return summary;
}

export interface SalesKpis {
  open: { proposals: number; lives: number };
  closedInMonth: { proposals: number; lives: number };
  lives: LivesSummary;
}

/** Os cartões que aparecem junto da comissão. */
export function kpisFor(proposals: ProposalLike[], month: string): SalesKpis {
  const open = proposals.filter((proposal) => proposal.status !== 'ENCERRADO');
  const closed = proposals.filter(
    (proposal) => proposal.status === 'ENCERRADO' && monthOf(proposal.effectiveDate) === month,
  );
  const livesOf = (list: ProposalLike[]) =>
    list.reduce((sum, proposal) => sum + (proposal.lives || proposal.beneficiaries.length), 0);
  return {
    open: { proposals: open.length, lives: livesOf(open) },
    closedInMonth: { proposals: closed.length, lives: livesOf(closed) },
    lives: summarizeLives(proposals),
  };
}

// ---------------------------------------------------------------- alertas

/** Parada: tempo demais no mesmo status (pelos limites do workspace). */
export function isStale(
  proposal: Pick<ProposalLike, 'status' | 'statusSince'>,
  staleDays: StaleDays = DEFAULT_SALES_SETTINGS.staleDays,
  now: Date = new Date(),
): boolean {
  if (proposal.status === 'ENCERRADO') return false;
  const limit = staleDays[proposal.status];
  const days = daysInStatus(proposal.statusSince, now);
  return limit !== undefined && days !== null && days >= limit;
}

/** Paradas, da mais antiga no status para a mais recente. */
export function staleProposals<T extends ProposalLike>(
  proposals: T[],
  staleDays: StaleDays = DEFAULT_SALES_SETTINGS.staleDays,
  now: Date = new Date(),
): T[] {
  return proposals
    .filter((proposal) => isStale(proposal, staleDays, now))
    .sort(
      (a, b) =>
        (daysInStatus(b.statusSince, now) ?? 0) - (daysInStatus(a.statusSince, now) ?? 0),
    );
}

/** Vigência chegando: não encerradas com vigência em até `windowDays` dias ou já vencida. */
export function upcomingEffective<T extends ProposalLike>(
  proposals: T[],
  today: string,
  windowDays = 14,
): T[] {
  return proposals
    .filter((proposal) => {
      if (proposal.status === 'ENCERRADO') return false;
      const days = daysUntil(proposal.effectiveDate, today);
      return days !== null && days <= windowDays;
    })
    .sort(
      (a, b) =>
        (daysUntil(a.effectiveDate, today) ?? 0) - (daysUntil(b.effectiveDate, today) ?? 0),
    );
}

// -------------------------------------------------------- normalização

/** Maiúsculas sem acento — para comparar textos da planilha. */
export const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .trim();

/** Tipo a partir do texto livre da planilha antiga. */
export function normalizeType(raw: unknown): ProposalType {
  const text = normalizeText(raw);
  if (
    text === 'PF' ||
    text.includes('PESSOA FISICA') ||
    text.includes('INDIVIDUAL') ||
    text.includes('FAMILIAR')
  ) {
    return 'PF';
  }
  if (text.includes('ADES') || text.includes('ALLCARE')) return 'ADESAO';
  return 'MPE';
}

/** Operadora deduzida do texto (tipo, observação, observações dos beneficiários). */
export function normalizeOperator(...texts: unknown[]): string {
  const all = texts.map(normalizeText);
  if (all.some((text) => text.includes('CEARA'))) return 'Unimed Ceará';
  const type = all[0] ?? '';
  if (type.includes('HAPVIDA')) return 'Hapvida';
  if (type.includes('AMIL')) return 'Amil';
  if (type.includes('BRADESCO')) return 'Bradesco';
  if (type.includes('SUL')) return 'SulAmérica';
  return 'Unimed Fortaleza';
}

const RELATIONSHIP =
  /^(TITULAR|FILH[OA](\(A\))?|ESPOS[OA](\(A\))?|C[ÔO]NJUGE|COMPANHEIR[OA](\(A\))?|ENTEAD[OA](\(A\))?|PAI|M[ÃA]E|IRM[ÃA]O?(\(A\))?|NET[OA](\(A\))?|AGREGAD[OA]|SOGR[OA])$/i;

/** Vínculo: o informado, ou o que veio escrito sozinho na observação ("FILHO(A)"). */
export function relationshipOf(relationship: unknown, notes: unknown): string {
  const given = String(relationship ?? '').trim();
  if (given) return given.toUpperCase();
  const match = String(notes ?? '')
    .trim()
    .match(RELATIONSHIP);
  return match ? match[1].toUpperCase() : '';
}

/** Junta um atalho ao texto da pendência com " · ", sem repetir. */
export function appendPending(text: string, shortcut: string): string {
  const parts = text
    .split(' · ')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.includes(shortcut)) parts.push(shortcut);
  return parts.join(' · ');
}

// ------------------------------------------------------------- validação

export interface ProposalDraft {
  operator: string | null | undefined;
  company: string | null | undefined;
  effectiveDate: string | null | undefined;
  status: ProposalStatus;
  pendingText?: string | null;
  beneficiaries: Array<{ name: string; situation: BeneficiarySituation | null | undefined }>;
}

export interface ValidationProblem {
  field: string;
  message: string;
  /** Posição do beneficiário, quando o problema é dele (a tela rola até ele). */
  index?: number;
}

/** O que o formulário exige para salvar. Lista vazia = pode salvar. */
export function validateProposal(
  draft: ProposalDraft,
  operators: string[] = DEFAULT_SALES_SETTINGS.operators,
): ValidationProblem[] {
  const problems: ValidationProblem[] = [];
  if (!draft.operator?.trim()) {
    problems.push({ field: 'operator', message: 'Escolha a operadora' });
  } else if (!operators.includes(draft.operator)) {
    problems.push({
      field: 'operator',
      message: `Operadora "${draft.operator}" não está na lista`,
    });
  }
  if (!draft.company?.trim()) {
    problems.push({ field: 'company', message: 'Informe a empresa ou o titular' });
  }
  if (!isValidDate(draft.effectiveDate)) {
    problems.push({ field: 'effectiveDate', message: 'Informe uma vigência válida' });
  }
  if (draft.status === 'PENDENTE' && !draft.pendingText?.trim()) {
    problems.push({ field: 'pendingText', message: 'Escreva qual é a pendência' });
  }
  const named = draft.beneficiaries.filter((beneficiary) => beneficiary.name.trim());
  if (named.length === 0) {
    problems.push({ field: 'beneficiaries', message: 'Inclua ao menos 1 beneficiário' });
  }
  draft.beneficiaries.forEach((beneficiary, index) => {
    if (beneficiary.name.trim() && !beneficiary.situation) {
      problems.push({
        field: 'beneficiaries',
        index,
        message: `Marque se ${beneficiary.name.trim()} é limpa, repique ou inadimplente`,
      });
    }
  });
  return problems;
}

/** Nº que entra na checagem de duplicidade (só dígitos; os genéricos ficam de fora). */
export function duplicateCheckNumber(requestNumber: string | null | undefined): string | null {
  const digits = String(requestNumber ?? '').replace(/\D/g, '');
  return digits && !GENERIC_REQUEST_NUMBERS.includes(digits) ? digits : null;
}

/** Chave de importação: reimportar a mesma linha atualiza em vez de duplicar. */
export function importKeyOf(
  requestNumber: string | null | undefined,
  effectiveDate: string | null | undefined,
  company: string | null | undefined,
): string {
  return [String(requestNumber ?? '').trim(), effectiveDate ?? '', normalizeText(company)]
    .join('|')
    .slice(0, 300);
}

// ------------------------------------------------------ planilha .xlsx

/** Ordem dos status: "o mais atrasado" é o de menor índice. */
export const statusOrder = (status: ProposalStatus): number =>
  PROPOSAL_STATUSES.indexOf(status);

/** Colunas da aba CADASTRO, por posição (layout da planilha do Marco). */
export const SHEET_HEADER = [
  'STATUS',
  'TIPO',
  'PLANO',
  'OBS',
  'SOLI',
  'EMPRESA',
  'NOME',
  'VENDEDOR',
  'VIGÊNCIA',
  'CPF',
  'V',
  'VALOR',
  'L/R',
  'OBS BENEF.',
];

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

/** "1.234,56", "502.06", "R$ -351,33" ou número → centavos (com sinal). */
export function moneyToCents(value: unknown): number {
  if (typeof value === 'number') return Math.round(value * 100);
  let cleaned = text(value)
    .replace(/[^\d,.-]/g, '')
    .replace(/,+/g, ',')
    .replace(/\.{2,}/g, '.');
  if (!cleaned) return 0;
  if (cleaned.includes(',')) cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

/** CPF só com dígitos; número do Excel perde os zeros à esquerda, então completa. */
export function cpfDigits(value: unknown): string | null {
  let digits = (typeof value === 'number' ? String(Math.round(value)) : text(value)).replace(
    /\D/g,
    '',
  );
  if (!digits) return null;
  if (digits.length < 11) digits = digits.padStart(11, '0');
  return digits.length === 11 ? digits : null;
}

/** Vigência da planilha: data do Excel (nº serial ou Date) ou "dd/mm/aaaa". */
export function sheetDate(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number') {
    return new Date(Date.UTC(1899, 11, 30) + Math.round(value) * DAY_MS)
      .toISOString()
      .slice(0, 10);
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text(value));
  if (iso) return isValidDate(iso[0]) ? iso[0] : null;
  const match = /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(text(value));
  if (!match) return null;
  const year = Number(match[3]) < 100 ? Number(match[3]) + 2000 : Number(match[3]);
  const date = `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  return isValidDate(date) ? date : null;
}

/** Status da coluna STATUS: ENC… → Encerrado, ATR… → Atribuído, o resto → Pré. */
export function sheetStatus(value: unknown): ProposalStatus {
  const status = normalizeText(value);
  if (status.startsWith('ENC')) return 'ENCERRADO';
  if (status.startsWith('ATR')) return 'ATRIBUIDO';
  return 'PRE';
}

export interface ImportedBeneficiary {
  name: string;
  cpf: string | null;
  monthlyCents: number;
  situation: BeneficiarySituation | null;
  relationship: string | null;
  notes: string | null;
}

/** Proposta pronta para gravar, vinda da planilha ou do artefato. */
export interface ImportedProposal {
  importKey: string;
  requestNumber: string | null;
  company: string;
  cnpj: string | null;
  effectiveDate: string;
  type: ProposalType;
  plan: string | null;
  operator: string;
  /** Nome do vendedor como está na origem (a coluna VENDEDOR). */
  sellerName: string;
  status: ProposalStatus;
  pendingText: string | null;
  notes: string | null;
  lives: number;
  livesManual: boolean;
  economyEnergyAt: string | null;
  statusSince: string | null;
  createdAt: string | null;
  history: Array<{ status: ProposalStatus; at: string }>;
  /** Entrevistas do artefato (horários de Fortaleza); a planilha não tem. */
  interviews: Array<{ who: string; at: string; notifyAt: string; done: boolean }>;
  beneficiaries: ImportedBeneficiary[];
}

export interface SheetReadResult {
  proposals: ImportedProposal[];
  /** Linhas que não viraram proposta, com o motivo. */
  skipped: Array<{ row: number; reason: string }>;
}

/**
 * Lê a aba CADASTRO: uma linha por beneficiário, agrupadas por (soli + vigência + empresa).
 * Mesmas regras do artefato: vale o status mais atrasado do grupo, valor negativo sem
 * L/R vira repique, nº genérico ("10", "1000") agrupa pelo nome do beneficiário.
 * `rows` vem sem o cabeçalho; `firstRow` é o nº da primeira linha na planilha.
 */
export function proposalsFromSheet(rows: unknown[][], firstRow = 2): SheetReadResult {
  const groups = new Map<string, ImportedProposal & { livesSum: number }>();
  const skipped: SheetReadResult['skipped'] = [];

  rows.forEach((row, index) => {
    const name = text(row[6]);
    const requestNumber = text(row[4]);
    if (!name && !requestNumber) return;
    const effectiveDate = sheetDate(row[8]);
    if (!effectiveDate) {
      skipped.push({ row: firstRow + index, reason: 'vigência vazia ou inválida' });
      return;
    }
    const companyColumn = text(row[5]);
    const generic = !requestNumber || GENERIC_REQUEST_NUMBERS.includes(requestNumber);
    const keyCompany = companyColumn || (generic ? name : '');
    const importKey = importKeyOf(requestNumber, effectiveDate, keyCompany);
    const status = sheetStatus(row[0]);

    let proposal = groups.get(importKey);
    if (!proposal) {
      proposal = {
        importKey,
        requestNumber: requestNumber || null,
        company: (companyColumn || name).toLocaleUpperCase('pt-BR'),
        cnpj: null,
        effectiveDate,
        type: normalizeType(row[1]),
        plan: text(row[2]) ? text(row[2]).toLocaleUpperCase('pt-BR') : null,
        operator: '',
        sellerName: text(row[7]).toLocaleUpperCase('pt-BR'),
        status,
        pendingText: null,
        notes: null,
        lives: 0,
        livesManual: false,
        economyEnergyAt: null,
        statusSince: null,
        createdAt: null,
        history: [],
        interviews: [],
        beneficiaries: [],
        livesSum: 0,
      };
      groups.set(importKey, proposal);
    }
    if (statusOrder(status) < statusOrder(proposal.status)) proposal.status = status;
    if (!proposal.plan && text(row[2])) proposal.plan = text(row[2]).toLocaleUpperCase('pt-BR');

    const cents = moneyToCents(row[11]);
    const flag = normalizeText(row[12]);
    const notes = [text(row[3]), text(row[13])].filter(Boolean).join(' · ');
    proposal.livesSum += typeof row[10] === 'number' ? row[10] : Number(text(row[10])) || 0;
    proposal.beneficiaries.push({
      name: name.toLocaleUpperCase('pt-BR'),
      cpf: cpfDigits(row[9]),
      monthlyCents: Math.abs(cents),
      situation: flag === 'R' || flag === 'IN' ? flag : cents < 0 ? 'R' : 'L',
      relationship: relationshipOf('', text(row[13])) || null,
      notes: notes || null,
    });
    // A observação da proposta pode dizer a operadora (ex.: "UNIMED CEARÁ").
    proposal.operator = normalizeOperator(
      row[1],
      row[3],
      ...proposal.beneficiaries.map((beneficiary) => beneficiary.notes),
    );
  });

  const proposals = [...groups.values()].map(({ livesSum, ...proposal }) => ({
    ...proposal,
    lives: livesSum || proposal.beneficiaries.length,
    livesManual: livesSum > 0 && livesSum !== proposal.beneficiaries.length,
  }));
  return { proposals, skipped };
}

const SHEET_STATUS: Partial<Record<ProposalStatus, string>> = {
  PRE: 'PRÉ',
  ATRIBUIDO: 'ATRIBUIDO',
  ENCERRADO: 'ENCERRADO',
};

const ddmmyyyy = (date: string | null): string =>
  date ? `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}` : '';

const formatCpf = (cpf: string | null): string =>
  cpf && cpf.length === 11
    ? `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`
    : (cpf ?? '');

export interface ExportableProposal {
  status: ProposalStatus;
  type: ProposalType;
  plan: string | null;
  notes: string | null;
  requestNumber: string | null;
  company: string;
  sellerName: string;
  effectiveDate: string | null;
  lives: number;
  beneficiaries: Array<{
    name: string;
    cpf: string | null;
    monthlyCents: number;
    situation: BeneficiarySituation | null;
    notes: string | null;
  }>;
}

/** Linhas da aba CADASTRO (com o cabeçalho), no layout da planilha, por vigência. */
export function sheetRowsFor(proposals: ExportableProposal[]): Array<Array<string | number>> {
  const rows: Array<Array<string | number>> = [SHEET_HEADER];
  [...proposals]
    .sort((a, b) => (a.effectiveDate ?? '').localeCompare(b.effectiveDate ?? ''))
    .forEach((proposal) => {
      const beneficiaries = proposal.beneficiaries.length
        ? proposal.beneficiaries
        : [{ name: '', cpf: null, monthlyCents: 0, situation: null, notes: null }];
      beneficiaries.forEach((beneficiary, index) => {
        rows.push([
          SHEET_STATUS[proposal.status] ?? proposal.status,
          proposal.type === 'ADESAO' ? 'ADESÃO' : proposal.type,
          proposal.plan ?? '',
          proposal.notes ?? '',
          proposal.requestNumber ?? '',
          proposal.company,
          beneficiary.name,
          proposal.sellerName,
          ddmmyyyy(proposal.effectiveDate),
          formatCpf(beneficiary.cpf),
          index === 0 ? proposal.lives : '',
          Math.abs(beneficiary.monthlyCents) / 100,
          beneficiary.situation ?? 'L',
          beneficiary.notes ?? '',
        ]);
      });
    });
  return rows;
}

// --------------------------------------------- migração do artefato (JSON)

/** Uma proposta como o artefato Minhas Vendas guarda (coleção `propostas`). */
export interface ArtifactProposal {
  id?: string;
  soli?: unknown;
  empresa?: unknown;
  cnpj?: unknown;
  vig?: unknown;
  tipo?: unknown;
  plano?: unknown;
  op?: unknown;
  vend?: unknown;
  vidas?: unknown;
  status?: unknown;
  pend?: unknown;
  obs?: unknown;
  benef?: Array<{
    nome?: unknown;
    cpf?: unknown;
    val?: unknown;
    lr?: unknown;
    par?: unknown;
    obs?: unknown;
  }>;
  stDesde?: unknown;
  hist?: Array<{ s?: unknown; em?: unknown }>;
  criado?: unknown;
  eco?: unknown;
  exemplo?: unknown;
  entrevistas?: Array<{
    id?: unknown;
    quem?: unknown;
    em?: unknown;
    avisar?: unknown;
    feito?: unknown;
  }>;
}

const isStatus = (value: unknown): value is ProposalStatus =>
  (PROPOSAL_STATUSES as readonly string[]).includes(String(value));

const isoOrNull = (value: unknown): string | null => {
  const parsed = Date.parse(text(value));
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
};

/** Converte as propostas do artefato. Exemplos e propostas sem vigência ficam de fora. */
export function proposalsFromArtifact(items: ArtifactProposal[]): SheetReadResult {
  const proposals: ImportedProposal[] = [];
  const skipped: SheetReadResult['skipped'] = [];
  items.forEach((item, index) => {
    if (item.exemplo) {
      skipped.push({ row: index + 1, reason: 'proposta de exemplo' });
      return;
    }
    const effectiveDate = isValidDate(text(item.vig)) ? text(item.vig) : sheetDate(item.vig);
    if (!effectiveDate) {
      skipped.push({ row: index + 1, reason: 'vigência vazia ou inválida' });
      return;
    }
    const beneficiaries: ImportedBeneficiary[] = (item.benef ?? [])
      .filter((beneficiary) => text(beneficiary.nome))
      .map((beneficiary) => {
        const flag = normalizeText(beneficiary.lr);
        return {
          name: text(beneficiary.nome).toLocaleUpperCase('pt-BR'),
          cpf: cpfDigits(beneficiary.cpf),
          monthlyCents: Math.abs(moneyToCents(beneficiary.val)),
          situation: flag === 'L' || flag === 'R' || flag === 'IN' ? flag : null,
          relationship: relationshipOf(beneficiary.par, beneficiary.obs) || null,
          notes: text(beneficiary.obs) || null,
        };
      });
    const status = isStatus(item.status) ? item.status : 'PRE';
    const typeText = normalizeText(item.tipo);
    const lives = Number(item.vidas) || beneficiaries.length;
    const cnpj = text(item.cnpj).replace(/\D/g, '');
    proposals.push({
      importKey: `artefato:${text(item.id) || `${index}`}`.slice(0, 300),
      requestNumber: text(item.soli) || null,
      company: (text(item.empresa) || beneficiaries[0]?.name || 'SEM NOME').toLocaleUpperCase(
        'pt-BR',
      ),
      cnpj: cnpj.length === 14 ? cnpj : null,
      effectiveDate,
      type: typeText === 'MPE' || typeText === 'PF' ? typeText : normalizeType(item.tipo),
      plan: text(item.plano) ? text(item.plano).toLocaleUpperCase('pt-BR') : null,
      operator:
        text(item.op) ||
        normalizeOperator(item.tipo, item.obs, ...(item.benef ?? []).map((b) => b.obs)),
      sellerName: text(item.vend).toLocaleUpperCase('pt-BR'),
      status,
      pendingText: status === 'PENDENTE' ? text(item.pend) || 'Pendência (migrada)' : null,
      notes: text(item.obs) || null,
      lives,
      livesManual: lives !== beneficiaries.length,
      economyEnergyAt: isValidDate(text(item.eco)) ? text(item.eco) : null,
      statusSince: isoOrNull(item.stDesde),
      createdAt: isoOrNull(item.criado),
      history: (item.hist ?? [])
        .filter((entry) => isStatus(entry.s) && isoOrNull(entry.em))
        .map((entry) => ({
          status: entry.s as ProposalStatus,
          at: isoOrNull(entry.em) as string,
        })),
      interviews: (item.entrevistas ?? [])
        .filter(
          (entry) =>
            isLocalDateTime(entry.em) &&
            isLocalDateTime(entry.avisar) &&
            String(entry.avisar) <= String(entry.em),
        )
        .map((entry) => ({
          who: text(entry.quem) || ALL_BENEFICIARIES,
          at: entry.em as string,
          notifyAt: entry.avisar as string,
          done: Boolean(entry.feito),
        })),
      beneficiaries,
    });
  });
  return { proposals, skipped };
}

// ------------------------------------------------- leitura do PDF (V2)

/** O que a leitura do PDF (ou a linha colada do sistema) entrega para o formulário. */
export interface ExtractedProposal {
  operator?: string;
  requestNumber?: string;
  company?: string;
  cnpj?: string;
  type?: ProposalType;
  plan?: string;
  effectiveDate?: string;
  beneficiaries?: Array<{
    name: string;
    cpf?: string;
    relationship?: string;
    monthlyCents?: number | null;
  }>;
}

const MONTHS_PT: Record<string, number> = {
  JANEIRO: 1,
  FEVEREIRO: 2,
  MARCO: 3,
  ABRIL: 4,
  MAIO: 5,
  JUNHO: 6,
  JULHO: 7,
  AGOSTO: 8,
  SETEMBRO: 9,
  OUTUBRO: 10,
  NOVEMBRO: 11,
  DEZEMBRO: 12,
};

/** CNPJ da própria Unimed Fortaleza, que aparece em todo contrato e não é o do cliente. */
const UNIMED_FORTALEZA_CNPJ = '05.868.278/0001-07';

/** Valor nos dois formatos que a Unimed usa: "351,33" e "502.06" / "349.4". */
export function unimedValueToCents(raw: string): number {
  const value = raw.replace(/[.,]$/, '');
  if (value.includes(',')) return moneyToCents(value);
  const parts = value.split('.');
  if (parts.length === 2 && parts[1].length <= 2) {
    return Math.round(Number.parseFloat(value) * 100) || 0;
  }
  return Math.round(Number.parseFloat(value.replace(/\./g, '')) * 100) || 0;
}

const RELATIONSHIP_IN_TEXT =
  /(FILHO\(A\)|FILH[OA]|ESPOS[OA](\(A\))?|C[ÔO]NJUGE|COMPANHEIR[OA](\(A\))?|ENTEAD[OA](\(A\))?|PAI|M[ÃA]E|IRM[ÃA]O?(\(A\))?|NET[OA](\(A\))?|AGREGAD[OA]|SOGR[OA])/;

type ExtractedPerson = NonNullable<ExtractedProposal['beneficiaries']>[number];

/**
 * Leitor direto do modelo de proposta da Unimed Fortaleza (contrato + ficha de inclusão),
 * sobre o texto que a tela reconstrói do PDF (linhas pela posição; colunas distantes viram
 * " | "). O nº da solicitação vem do código "#ASS…<dígitos>", não do "N.º" do cabeçalho.
 */
export function parseUnimedText(source: string): ExtractedProposal {
  const lines = source
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !/^--- página/.test(line) && !/^[A-Z]$/.test(line));
  const cells = (line: string) =>
    line
      .split(' | ')
      .map((cell) => cell.trim())
      .filter(Boolean);
  const after = (pattern: RegExp) => {
    for (let index = 0; index < lines.length - 1; index += 1) {
      if (pattern.test(lines[index])) return { index, next: lines[index + 1] };
    }
    return null;
  };
  const result: ExtractedProposal = {};

  const request = /#ASS[A-Z]*?(\d{3,})/.exec(source);
  if (request) result.requestNumber = request[1];
  const effective = normalizeText(source).match(
    /DATA DE VIGENCIA DO CONTRATO:?\s*(\d{1,2}) DE ([A-Z]+) DE (\d{4})/,
  );
  if (effective && MONTHS_PT[effective[2]]) {
    const month = String(MONTHS_PT[effective[2]]).padStart(2, '0');
    result.effectiveDate = `${effective[3]}-${month}-${effective[1].padStart(2, '0')}`;
  }
  const company = after(/NOME DA EMPRESA CONTRATANTE/) ?? after(/^EMPRESA( \| CNPJ)?$/);
  if (company) result.company = cells(company.next)[0];
  const cnpj = (source.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/g) ?? []).find(
    (value) => value !== UNIMED_FORTALEZA_CNPJ,
  );
  if (cnpj) result.cnpj = cnpj.replace(/\D/g, '');
  const plan = after(/^NOME DO PLANO/);
  if (plan) {
    for (const line of lines.slice(plan.index + 1, plan.index + 4)) {
      const cell = cells(line).find(
        (value) =>
          !/^\(/.test(value) &&
          !/^(C[ÓO]DIGO|REGISTRO|ABRANG|VALOR|SM\d)/.test(value) &&
          /[A-Z]{3,}/.test(value),
      );
      if (cell) {
        result.plan = cell;
        break;
      }
    }
  }
  if (/COLETIVO EMPRESARIAL/.test(source)) result.type = 'MPE';
  if (/UNIMED DE FORTALEZA/i.test(source)) result.operator = 'Unimed Fortaleza';

  const people: ExtractedPerson[] = [];
  let current: ExtractedPerson | null = null;
  lines.forEach((line, index) => {
    const startsPerson =
      /^NOME DO COLABORADOR\b/.test(line) ||
      (/^NOME( \| CPF)?$/.test(line) &&
        index > 0 &&
        /^DEPENDENTE \d+/.test(lines[index - 1])) ||
      (/^NOME \| CPF/.test(line) && index > 0 && /DEPENDENTE/.test(lines[index - 1]));
    if (startsPerson) {
      const values = cells(lines[index + 1] ?? '');
      const cpf = values.find((value) => /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(value)) ?? '';
      current = {
        name: values[0] ?? '',
        cpf: cpf.replace(/\D/g, ''),
        relationship: /COLABORADOR/.test(line) ? 'TITULAR' : '',
        monthlyCents: null,
      };
      people.push(current);
      return;
    }
    const person = current as ExtractedPerson | null;
    if (!person) return;
    if (/^PARENTESCO\b|\| PARENTESCO\b/.test(line) && !person.relationship) {
      const match = RELATIONSHIP_IN_TEXT.exec(lines[index + 1] ?? '');
      if (match) person.relationship = match[1];
    }
    if (person.monthlyCents === null) {
      const value = /R\$\s*(\d[\d.,]*)/.exec(line);
      if (value && !/TOTAL/.test(line)) person.monthlyCents = unimedValueToCents(value[1]);
    }
  });
  result.beneficiaries = people.filter(
    (person) => person.name && !/^(CPF|NOME)/.test(person.name),
  );
  return result;
}

/** Leitura completa: nº, vigência e beneficiários com nome. Sem isso, vai para a IA. */
export const isCompleteExtraction = (data: ExtractedProposal): boolean =>
  Boolean(
    data.requestNumber &&
    data.effectiveDate &&
    data.beneficiaries?.length &&
    data.beneficiaries.every((person) => person.name),
  );

/**
 * A linha de resultado do sistema da operadora: "Solicitação: 23574 - Movimentação: … -
 * Empresa: … - Beneficiário: … - Status: … - Vendedor: … - Vigência: 30/10/2026".
 */
export function parseSystemLine(line: string): Pick<
  ExtractedProposal,
  'requestNumber' | 'company' | 'effectiveDate'
> & {
  holder?: string;
} {
  const value = String(line ?? '');
  const get = (pattern: RegExp) => pattern.exec(value)?.[1]?.trim() || undefined;
  const result: ReturnType<typeof parseSystemLine> = {
    requestNumber: get(/Solicita[çc][ãa]o:\s*(\d+)/i),
    company: get(/Empresa:\s*(.+?)\s+-\s+Benefici/i),
    holder: get(/Benefici[áa]rio:\s*(.+?)\s+-\s+Status/i),
  };
  const date = /Vig[êe]ncia:\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/i.exec(value);
  if (date) {
    result.effectiveDate = `${date[3]}-${date[2].padStart(2, '0')}-${date[1].padStart(2, '0')}`;
  }
  return result;
}

/** Operadora pelo nome livre que veio do PDF. */
function operatorFromText(raw: unknown, operators: string[]): string | undefined {
  const name = normalizeText(raw);
  const exact = operators.find((operator) => normalizeText(operator) === name);
  if (exact) return exact;
  const guess = name.includes('CEARA')
    ? 'Unimed Ceará'
    : name.includes('UNIMED')
      ? 'Unimed Fortaleza'
      : name.includes('HAP')
        ? 'Hapvida'
        : name.includes('AMIL')
          ? 'Amil'
          : name.includes('BRADESCO')
            ? 'Bradesco'
            : name.includes('SUL')
              ? 'SulAmérica'
              : undefined;
  return guess && operators.includes(guess) ? guess : undefined;
}

/** O JSON que a IA devolve (formato do prompt do artefato) → o que o formulário entende. */
export function fromAiExtraction(raw: unknown, operators: string[]): ExtractedProposal {
  const data = (raw ?? {}) as Record<string, unknown>;
  const effective = text(data.vigencia);
  const people = Array.isArray(data.beneficiarios) ? data.beneficiarios : [];
  return {
    operator: operatorFromText(data.operadora, operators),
    requestNumber: text(data.proposta).replace(/\s/g, '') || undefined,
    company: text(data.empresa) || undefined,
    cnpj: text(data.cnpj).replace(/\D/g, '') || undefined,
    type: text(data.tipo) ? normalizeType(data.tipo) : undefined,
    plan: text(data.plano) || undefined,
    effectiveDate: isValidDate(effective) ? effective : undefined,
    beneficiaries: people
      .map((item) => item as Record<string, unknown>)
      .filter((item) => text(item.nome))
      .map((item) => ({
        name: text(item.nome),
        cpf: cpfDigits(item.cpf) ?? undefined,
        relationship: text(item.parentesco).toUpperCase() || undefined,
        monthlyCents:
          typeof item.valor === 'number'
            ? Math.round(Math.abs(item.valor) * 100)
            : text(item.valor)
              ? Math.abs(moneyToCents(item.valor))
              : null,
      })),
  };
}

/** Instruções para a IA ler a proposta (as mesmas regras do artefato). */
export const PDF_AI_PROMPT = `Você vai ler uma proposta de adesão a plano de saúde (Unimed Fortaleza, Hapvida, Amil ou outra operadora) preenchida por um corretor em Fortaleza. Extraia os dados no formato pedido.
- operadora: Unimed Fortaleza, Unimed Ceará, Hapvida, Amil, Bradesco, SulAmérica ou outra, como estiver no documento.
- proposta: número da proposta ou solicitação. Na Unimed Fortaleza o número da SOLICITAÇÃO é o que aparece no código "#ASS..." perto da data de vigência (ex.: "#ASSFPME23574" → "23574"); NÃO use o "N.º" do cabeçalho do contrato.
- empresa: razão social do contratante; se for plano individual/familiar, o nome do titular.
- cnpj: CNPJ do contratante, se houver.
- tipo: MPE (empresarial/PME), PF (individual/familiar) ou ADESÃO (coletivo por adesão).
- plano: nome do produto/plano contratado.
- vigencia: AAAA-MM-DD. Na Unimed aparece como "DATA DE VIGÊNCIA DO CONTRATO".
- beneficiarios: titulares e dependentes, na ordem da proposta. "parentesco" é TITULAR para o titular/colaborador; para dependente, o parentesco escrito (FILHO(A), CÔNJUGE etc.). "valor" é a mensalidade daquele beneficiário em reais, como número com ponto decimal. "cpf" só com dígitos.
Não invente nada: use "" (ou null no valor) quando o dado não estiver no documento.`;

/** Esquema da resposta da IA (saída estruturada). */
export const PDF_AI_SCHEMA = {
  type: 'object',
  properties: {
    operadora: { type: 'string' },
    proposta: { type: 'string' },
    empresa: { type: 'string' },
    cnpj: { type: 'string' },
    tipo: { type: 'string' },
    plano: { type: 'string' },
    vigencia: { type: 'string' },
    beneficiarios: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          nome: { type: 'string' },
          cpf: { type: 'string' },
          parentesco: { type: 'string' },
          valor: { type: ['number', 'null'] },
        },
        required: ['nome', 'cpf', 'parentesco', 'valor'],
        additionalProperties: false,
      },
    },
  },
  required: [
    'operadora',
    'proposta',
    'empresa',
    'cnpj',
    'tipo',
    'plano',
    'vigencia',
    'beneficiarios',
  ],
  additionalProperties: false,
};

// ------------------------------------------ entrevistas médicas (V3)

/**
 * Horário "de parede" de Fortaleza, `AAAA-MM-DDTHH:MM` — como o vendedor digita e lê.
 * Fortaleza não tem horário de verão: o fuso é sempre UTC-3.
 */
const FORTALEZA_OFFSET = '-03:00';
const FORTALEZA_OFFSET_MS = -3 * 60 * 60 * 1000;

export const isLocalDateTime = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) &&
  isValidDate(value.slice(0, 10)) &&
  Number(value.slice(11, 13)) < 24 &&
  Number(value.slice(14, 16)) < 60;

/** "2026-10-30T14:30" (Fortaleza) → instante. */
export const fromFortalezaLocal = (local: string): Date =>
  new Date(`${local}:00${FORTALEZA_OFFSET}`);

/** Instante → "2026-10-30T14:30" em Fortaleza. */
export const toFortalezaLocal = (date: Date | string): string =>
  new Date(new Date(date).getTime() + FORTALEZA_OFFSET_MS).toISOString().slice(0, 16);

/** "30/10 14:30". */
export const formatLocalShort = (local: string): string =>
  `${local.slice(8, 10)}/${local.slice(5, 7)} ${local.slice(11, 16)}`;

/** Quando avisar o vendedor, como no artefato. `x` = data e hora livres. */
export const NOTIFY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0', label: 'Na hora da entrevista' },
  { value: '10', label: '10 minutos antes' },
  { value: '20', label: '20 minutos antes' },
  { value: '30', label: '30 minutos antes' },
  { value: '60', label: '1 hora antes' },
  { value: '120', label: '2 horas antes' },
  { value: 'm8', label: 'No mesmo dia às 8h' },
  { value: 'd18', label: 'No dia anterior às 18h' },
  { value: 'x', label: 'Escolher dia e hora' },
];

/** O horário do aviso (Fortaleza) para a opção escolhida. */
export function notifyAtFor(
  interviewAt: string,
  option: string,
  custom?: string,
): string | null {
  if (option === 'x') return isLocalDateTime(custom) ? custom : null;
  if (option === 'm8') return `${interviewAt.slice(0, 10)}T08:00`;
  if (option === 'd18') {
    const dayBefore = new Date(Date.parse(`${interviewAt.slice(0, 10)}T00:00:00Z`) - DAY_MS);
    return `${dayBefore.toISOString().slice(0, 10)}T18:00`;
  }
  const minutes = Number(option);
  if (!Number.isFinite(minutes)) return null;
  return toFortalezaLocal(
    new Date(fromFortalezaLocal(interviewAt).getTime() - minutes * 60_000),
  );
}

/** O que o formulário de entrevista exige. Vazio = pode salvar. */
export function validateInterview(at: string, notifyAt: string | null): string | null {
  if (!isLocalDateTime(at)) return 'Informe a data e a hora da entrevista.';
  if (!notifyAt || !isLocalDateTime(notifyAt)) return 'Escolha quando quer ser avisado.';
  if (notifyAt > at) return 'O aviso precisa ser antes da entrevista.';
  return null;
}

/** Link do Google Agenda: evento de 15 min na hora do aviso, fuso de Fortaleza. */
export function googleCalendarLink(input: {
  who: string;
  company: string;
  requestNumber: string | null;
  at: string;
  notifyAt: string;
}): string {
  const stamp = (local: string) => `${local.replace(/[-:]/g, '')}00`;
  const end = toFortalezaLocal(
    new Date(fromFortalezaLocal(input.notifyAt).getTime() + 15 * 60_000),
  );
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Lembrar entrevista médica: ${input.who} (${input.company})`,
    dates: `${stamp(input.notifyAt)}/${stamp(end)}`,
    ctz: SALES_TIME_ZONE,
    details: `Entrevista com a enfermeira em ${formatLocalShort(input.at)}. Proposta ${input.requestNumber ?? ''}.`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** "Todos da proposta" quando há mais de um beneficiário. */
export const ALL_BENEFICIARIES = 'Todos da proposta';
